/**
 * ReceitaFormDialog Component
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Trash2 } from "lucide-react";
import { receitaSchema, type ReceitaFormData, type ReceitaIngredienteFormData } from "@/schemas/receitaSchema";
import { receitasAdapter, productsAdapter } from "@/data";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { ReceitaCompleta } from "@/types/receita";

interface ProdutoCustoUnitario {
  produto_id: string;
  sem_custo_recente: boolean;
  custo_por_grama?: number;
  custo_por_ml?: number;
  custo_por_unidade?: number;
}

interface ReceitaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  receita: ReceitaCompleta | null;
  onSuccess: () => void;
}

export function ReceitaFormDialog({ open, onOpenChange, mode, receita, onSuccess }: ReceitaFormDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [ingredientes, setIngredientes] = useState<ReceitaIngredienteFormData[]>([]);
  const [produtos, setProdutos] = useState<any[]>([]);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ReceitaFormData>({
    resolver: zodResolver(receitaSchema),
    defaultValues: {
      dificuldade: "media",
      custo_energia: 0,
    },
  });

  // Load produtos
  useEffect(() => {
    if (open) {
      productsAdapter.list({ limit: 1000 }).then((res) => setProdutos(res.items));
    }
  }, [open]);

  // Load receita data for edit mode
  useEffect(() => {
    if (open && mode === "edit" && receita) {
      const r = receita.receita;
      reset({
        nome: r.nome,
        categoria: r.categoria,
        subcategoria: r.subcategoria,
        porcao: r.porcao,
        dificuldade: r.dificuldade,
        rendimento: r.rendimento,
        tamanho: r.tamanho,
        tempo_preparo_minutos: r.tempo_preparo_minutos,
        tempo_forno_minutos: r.tempo_forno_minutos,
        temperatura_forno_celsius: r.temperatura_forno_celsius,
        tempo_descanso_minutos: r.tempo_descanso_minutos,
        tempo_total_minutos: r.tempo_total_minutos,
        modo_preparo: r.modo_preparo,
        origem_receita: r.origem_receita,
        observacoes_receita: r.observacoes_receita,
        observacoes_internas: r.observacoes_internas,
        custo_energia: r.custo_energia,
        modo_armazenamento: r.modo_armazenamento,
        validade_dias: r.validade_dias,
        observacoes_entrega: r.observacoes_entrega,
        versao: r.versao,
            ingredientes: receita.ingredientes.map((i) => ({
              produto_id: i.produto_id,
              produto_nome: i.produto_nome,
              peso_gramas: i.peso_gramas,
              volume_ml: i.volume_ml,
              quantidade_unidades: i.quantidade_unidades,
              desperdicio_percentual: i.desperdicio_percentual,
              ordem_preparo: i.ordem_preparo,
              tipo_ingrediente: i.tipo_ingrediente,
              observacoes: i.observacoes,
              alergenos: i.alergenos,
              substituivel: i.substituivel,
              opcional_receita: i.opcional_receita,
            })),
      });
      setIngredientes(receita.ingredientes.map((i) => ({
        produto_id: i.produto_id,
        produto_nome: i.produto_nome,
        peso_gramas: i.peso_gramas,
        volume_ml: i.volume_ml,
        quantidade_unidades: i.quantidade_unidades,
        desperdicio_percentual: i.desperdicio_percentual,
        ordem_preparo: i.ordem_preparo,
        tipo_ingrediente: i.tipo_ingrediente,
        observacoes: i.observacoes,
        alergenos: i.alergenos,
        substituivel: i.substituivel,
        opcional_receita: i.opcional_receita,
      })));
    } else if (open && mode === "create") {
      reset({ dificuldade: "media", custo_energia: 0, ingredientes: [] });
      setIngredientes([]);
    }
  }, [open, mode, receita, reset]);

  const addIngrediente = () => {
    setIngredientes([...ingredientes, { 
      produto_id: "", 
      produto_nome: "",
      peso_gramas: undefined,
      volume_ml: undefined,
      quantidade_unidades: undefined,
      desperdicio_percentual: 0,
      tipo_ingrediente: "",
      ordem_preparo: "",
      observacoes: "",
      alergenos: "",
      substituivel: "",
      opcional_receita: "",
    }]);
  };

  const removeIngrediente = (index: number) => {
    setIngredientes(ingredientes.filter((_, i) => i !== index));
  };

  // Sincronizar ingredientes com o formulário
  useEffect(() => {
    setValue("ingredientes", ingredientes);
  }, [ingredientes, setValue]);

  // Função para calcular custos dos ingredientes
  const calcularCustos = async (ingredientesData: ReceitaIngredienteFormData[]) => {
    try {
      // Buscar custos unitários de todos os produtos
      const produtoIds = ingredientesData.map((i) => i.produto_id).filter(Boolean);
      if (produtoIds.length === 0) {
        return { custoIngredientes: 0, custoTotal: 0, produtosSemCusto: [] };
      }

      const custosData = await productsAdapter.getProdutoCustosUnitarios(produtoIds);
      const custosMap = new Map(custosData.map((c: ProdutoCustoUnitario) => [c.produto_id, c]));

      let custoTotal = 0;
      const produtosSemCusto: string[] = [];

      // Calcular custo de cada ingrediente
      for (const ing of ingredientesData) {
        const custoInfo = custosMap.get(ing.produto_id) as ProdutoCustoUnitario | undefined;
        
        if (!custoInfo || custoInfo.sem_custo_recente) {
          produtosSemCusto.push(ing.produto_nome);
          continue;
        }

        let custoIngrediente = 0;

        // Calcular baseado no tipo de medição
        if (ing.peso_gramas && ing.peso_gramas > 0) {
          custoIngrediente = ing.peso_gramas * (custoInfo.custo_por_grama || 0);
        } else if (ing.volume_ml && ing.volume_ml > 0) {
          custoIngrediente = ing.volume_ml * (custoInfo.custo_por_ml || 0);
        } else if (ing.quantidade_unidades && ing.quantidade_unidades > 0) {
          custoIngrediente = ing.quantidade_unidades * (custoInfo.custo_por_unidade || 0);
        }

        // Aplicar desperdício
        if (ing.desperdicio_percentual && ing.desperdicio_percentual > 0) {
          custoIngrediente *= (1 + ing.desperdicio_percentual / 100);
        }

        custoTotal += custoIngrediente;
      }

      return { 
        custoIngredientes: custoTotal, 
        custoTotal, 
        produtosSemCusto 
      };
    } catch (error) {
      console.error("Erro ao calcular custos:", error);
      return { custoIngredientes: 0, custoTotal: 0, produtosSemCusto: [] };
    }
  };

  const onSubmit = async (data: ReceitaFormData) => {
    if (ingredientes.length === 0) {
      toast({ title: "Erro", description: "Adicione pelo menos um ingrediente.", variant: "destructive" });
      return;
    }

    try {
      setIsSaving(true);

      // ✅ Verificar autenticação antes de processar
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        toast({ 
          title: "❌ Erro de autenticação", 
          description: "Você precisa estar logado para salvar uma receita. Faça login e tente novamente.",
          variant: "destructive",
          duration: 7000,
        });
        setIsSaving(false);
        return;
      }

      // Calcular custos antes de enviar
      const { custoIngredientes, custoTotal, produtosSemCusto } = await calcularCustos(ingredientes);

      // Incluir custos no payload
      const payload = { 
        receita: {
          ...data,
          custo_ingredientes: custoIngredientes,
          custo_total: custoTotal + (data.custo_energia || 0),
        }, 
        ingredientes 
      };

      if (mode === "create") {
        await receitasAdapter.create(payload);
      } else if (receita) {
        await receitasAdapter.update(receita.receita.id_receita, payload);
      }

      // Toast de sucesso
      toast({ 
        title: "Receita salva com sucesso!", 
        description: custoTotal > 0 
          ? `Custo calculado: R$ ${custoTotal.toFixed(2)}` 
          : undefined,
      });

      // Toast de aviso para produtos sem custo
      if (produtosSemCusto.length > 0) {
        toast({ 
          title: "⚠️ Produtos sem custo recente", 
          description: `Os seguintes produtos não têm entradas nos últimos 30 dias: ${produtosSemCusto.join(", ")}`,
          variant: "default",
          duration: 8000,
        });
      }

      // ✅ Fechar diálogo e atualizar lista apenas após sucesso
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("❌ Erro ao salvar receita:", error);
      console.error("❌ Detalhes do erro:", {
        tipo: typeof error,
        mensagem: error instanceof Error ? error.message : String(error),
        mode,
        receitaId: receita?.receita.id_receita,
      });
      
      const errorMsg = error instanceof Error ? error.message : "Não foi possível salvar a receita.";
      
      toast({ 
        title: "Erro ao salvar receita", 
        description: errorMsg,
        variant: "destructive",
        duration: 7000,
      });
      // ✅ Não fechar o diálogo em caso de erro
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nova Receita" : "Editar Receita"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Accordion type="multiple" defaultValue={["basico", "ingredientes"]}>
            <AccordionItem value="basico">
              <AccordionTrigger>Informações Básicas</AccordionTrigger>
              <AccordionContent className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    {...register("nome")}
                    placeholder="Nome da receita"
                  />
                  {errors.nome && (
                    <p className="text-sm text-destructive mt-1">{errors.nome.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="categoria">Categoria</Label>
                    <Input
                      id="categoria"
                      {...register("categoria")}
                      placeholder="Ex: Bolos"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subcategoria">Subcategoria</Label>
                    <Input
                      id="subcategoria"
                      {...register("subcategoria")}
                      placeholder="Ex: Recheados, Simples"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="dificuldade">Dificuldade</Label>
                  <Input
                    id="dificuldade"
                    {...register("dificuldade")}
                    placeholder="Fácil, Média, Difícil"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rendimento">Rendimento</Label>
                    <Input
                      id="rendimento"
                      type="number"
                      step="0.01"
                      {...register("rendimento", { valueAsNumber: true })}
                      placeholder="Ex: 12"
                    />
                    {errors.rendimento && (
                      <p className="text-sm text-destructive mt-1">{errors.rendimento.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="tamanho">Tamanho</Label>
                    <Select
                      value={watch("tamanho") || ""}
                      onValueChange={(value) => setValue("tamanho", value as any)}
                    >
                      <SelectTrigger id="tamanho">
                        <SelectValue placeholder="Selecione o tamanho" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mini_30g">Mini (30g)</SelectItem>
                        <SelectItem value="pequeno_120g">Pequeno (120g)</SelectItem>
                        <SelectItem value="medio_500g">Médio (500g)</SelectItem>
                        <SelectItem value="grande_1kg">Grande (1kg)</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.tamanho && (
                      <p className="text-sm text-destructive mt-1">{errors.tamanho.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="porcao">Porção</Label>
                    <Input
                      id="porcao"
                      {...register("porcao")}
                      placeholder="Ex: 10 fatias"
                    />
                  </div>

                  <div>
                    <Label htmlFor="versao">Versão</Label>
                    <Input
                      id="versao"
                      {...register("versao")}
                      placeholder="Ex: 1.0, 2.5, Final"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-medium">Tempos e Temperaturas</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tempo_preparo_minutos">Tempo de Preparo (min)</Label>
                      <Input
                        id="tempo_preparo_minutos"
                        type="text"
                        {...register("tempo_preparo_minutos")}
                        placeholder="Ex: 30, 45-60, cerca de 1h"
                      />
                    </div>

                    <div>
                      <Label htmlFor="tempo_forno_minutos">Tempo de Forno (min)</Label>
                      <Input
                        id="tempo_forno_minutos"
                        type="text"
                        {...register("tempo_forno_minutos")}
                        placeholder="Ex: 40, 35-45"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="temperatura_forno_celsius">Temperatura do Forno (°C)</Label>
                      <Input
                        id="temperatura_forno_celsius"
                        type="text"
                        {...register("temperatura_forno_celsius")}
                        placeholder="Ex: 180, 180-200"
                      />
                    </div>

                    <div>
                      <Label htmlFor="tempo_descanso_minutos">Tempo de Descanso (min)</Label>
                      <Input
                        id="tempo_descanso_minutos"
                        type="text"
                        {...register("tempo_descanso_minutos")}
                        placeholder="Ex: 20, 15-30"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="tempo_total_minutos">Tempo Total de Preparo (min)</Label>
                    <Input
                      id="tempo_total_minutos"
                      type="text"
                      {...register("tempo_total_minutos")}
                      placeholder="Ex: 120, aproximadamente 2h"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Tempo total do início ao fim do preparo
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="modo_preparo">Modo de Preparo</Label>
                  <Textarea
                    id="modo_preparo"
                    {...register("modo_preparo")}
                    placeholder="Instruções passo a passo"
                    rows={4}
                    className="resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="modo_armazenamento">Modo de Armazenamento</Label>
                    <Input
                      id="modo_armazenamento"
                      {...register("modo_armazenamento")}
                      placeholder="Ex: Refrigerado"
                    />
                  </div>

                  <div>
                    <Label htmlFor="validade_dias">Validade (dias)</Label>
                    <Input
                      id="validade_dias"
                      type="text"
                      {...register("validade_dias")}
                      placeholder="Ex: 3, 5-7, até 1 semana"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="origem_receita">Origem da Receita</Label>
                  <Input
                    id="origem_receita"
                    {...register("origem_receita")}
                    placeholder="Ex: Receita da Vó Maria, Livro Tal"
                  />
                </div>

                <div>
                  <Label htmlFor="observacoes_receita">Observações</Label>
                  <Textarea
                    id="observacoes_receita"
                    {...register("observacoes_receita")}
                    placeholder="Observações gerais sobre a receita"
                    rows={3}
                    className="resize-none"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="ingredientes">
              <AccordionTrigger>Ingredientes ({ingredientes.length})</AccordionTrigger>
              <AccordionContent className="space-y-4">
                <Button type="button" variant="outline" onClick={addIngrediente}>
                  <Plus className="mr-2 h-4 w-4" /> Adicionar Ingrediente
                </Button>
                
                {ingredientes.length > 0 ? (
                  <div className="space-y-4">
                    {ingredientes.map((ing, idx) => (
                      <div key={idx} className="p-4 border rounded-lg space-y-4 bg-card">
                        {/* Linha 1: Produto + Categoria */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Produto *</Label>
                            <Select
                              value={ing.produto_id}
                              onValueChange={(value) => {
                                const produto = produtos.find((p) => p.id_produto === value);
                                const n = [...ingredientes];
                                n[idx].produto_id = value;
                                n[idx].produto_nome = produto?.nome?.trim() || "";
                                setIngredientes(n);
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione um produto" />
                              </SelectTrigger>
                              <SelectContent>
                                {produtos.map((p) => (
                                  <SelectItem key={p.id_produto} value={p.id_produto}>
                                    {p.nome}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label>Categoria do Produto</Label>
                            <Input 
                              value={produtos.find(p => p.id_produto === ing.produto_id)?.categoria?.nome || "—"} 
                              disabled 
                              className="bg-muted"
                            />
                          </div>
                        </div>
                        
                        {/* Linha 2: Medições (apenas 1 permitido) */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label>Peso (g)</Label>
                            <Input
                              type="number"
                              step="0.001"
                              value={ing.peso_gramas || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].peso_gramas = e.target.value ? Number(e.target.value) : undefined;
                                n[idx].volume_ml = undefined;
                                n[idx].quantidade_unidades = undefined;
                                setIngredientes(n);
                              }}
                              placeholder="Ex: 250"
                            />
                          </div>
                          
                          <div>
                            <Label>Volume (ml)</Label>
                            <Input
                              type="number"
                              step="0.001"
                              value={ing.volume_ml || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].volume_ml = e.target.value ? Number(e.target.value) : undefined;
                                n[idx].peso_gramas = undefined;
                                n[idx].quantidade_unidades = undefined;
                                setIngredientes(n);
                              }}
                              placeholder="Ex: 500"
                            />
                          </div>
                          
                          <div>
                            <Label>Quantidade (un.)</Label>
                            <Input
                              type="number"
                              step="0.001"
                              value={ing.quantidade_unidades || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].quantidade_unidades = e.target.value ? Number(e.target.value) : undefined;
                                n[idx].peso_gramas = undefined;
                                n[idx].volume_ml = undefined;
                                setIngredientes(n);
                              }}
                              placeholder="Ex: 3"
                            />
                          </div>
                        </div>
                        
                        {/* Linha 3: Desperdício + Tipo + Ordem */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label>Desperdício (%)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={ing.desperdicio_percentual || 0}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].desperdicio_percentual = Number(e.target.value);
                                setIngredientes(n);
                              }}
                              placeholder="Ex: 3"
                            />
                          </div>
                          
                          <div>
                            <Label>Tipo de Item</Label>
                            <Input
                              value={ing.tipo_ingrediente || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].tipo_ingrediente = e.target.value;
                                setIngredientes(n);
                              }}
                              placeholder="Ingrediente, Recheio, Cobertura..."
                            />
                          </div>
                          
                          <div>
                            <Label>Ordem de Preparo</Label>
                            <Input
                              value={ing.ordem_preparo || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].ordem_preparo = e.target.value;
                                setIngredientes(n);
                              }}
                              placeholder="Ex: Adicionar ao final"
                            />
                          </div>
                        </div>
                        
                        {/* Linha 4: Observações + Alérgenos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Observações do Item</Label>
                            <Input
                              value={ing.observacoes || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].observacoes = e.target.value;
                                setIngredientes(n);
                              }}
                              placeholder="Ex: peneirar, temperar antes"
                            />
                          </div>
                          
                          <div>
                            <Label>Alérgenos</Label>
                            <Input
                              value={ing.alergenos || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].alergenos = e.target.value;
                                setIngredientes(n);
                              }}
                              placeholder="Ex: leite, ovos, glúten"
                            />
                          </div>
                        </div>
                        
                        {/* Linha 5: Substituível + Opcional */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Substituível?</Label>
                            <Input
                              value={ing.substituivel || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].substituivel = e.target.value;
                                setIngredientes(n);
                              }}
                              placeholder="Ex: Pode usar margarina"
                            />
                          </div>
                          
                          <div>
                            <Label>Opcional na receita?</Label>
                            <Input
                              value={ing.opcional_receita || ""}
                              onChange={(e) => {
                                const n = [...ingredientes];
                                n[idx].opcional_receita = e.target.value;
                                setIngredientes(n);
                              }}
                              placeholder="Ex: Sim, para decoração"
                            />
                          </div>
                        </div>
                        
                        {/* Botão Remover */}
                        <div className="flex justify-end">
                          <Button 
                            type="button" 
                            variant="destructive" 
                            size="sm" 
                            onClick={() => removeIngrediente(idx)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Remover
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum ingrediente adicionado.</p>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancelar</Button>
            <Button type="submit" disabled={isSaving}>{isSaving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
